/**
 * @module components/Checkout/Inputs
 * @description Individual input components for checkout form.
 * Includes text inputs, country/territory selects, international phone inputs, and submit button.
 */

import React, { useCallback, useEffect, useState, useRef } from 'react';
import Select from 'react-select';
import cx from 'classnames';
import countries from '@/modules/countryCodes.json';
import territories from '@/modules/territories.json';
import useIntlTelInput from '@/modules/useIntlTelInput';
import { useAccordionContext } from '@/components/Accordion';
import { useField } from './useCheckoutForm';

/**
 * @typedef InputProps
 * @property {string} [type] - Input type (text, textarea, etc.)
 * @property {Function} validate - Validation function
 * @property {Function} setValue - Value setter function
 * @property {string|React.ReactNode} label - Field label
 * @property {string} field - Field name
 * @property {string} [value] - Current field value
 * @property {Record<string, unknown>} [other] - Additional HTML attributes
 */

/**
 * @component Input
 * @summary Basic input or textarea field with validation and cursor position preservation.
 * Integrates with accordion context for keyboard navigation management.
 * @param {InputProps} props - Component props
 * @returns {React.ReactElement} Input or textarea element
 */
const Input = ({ type, validate, setValue, label, field, value, ...other }) => {
  const cursor = useRef(0);
  const inputRef = useRef();
  useEffect(() => {
    inputRef.current.selectionStart = cursor.current;
    inputRef.current.selectionEnd = cursor.current;
  }, [value]);

  const accordion = useAccordionContext();

  const inputProps = {
    ...other,
    className: 'auth__input s16 x',
    name: field,
    placeholder: label,
    onChange: useCallback(
      (ev) => {
        cursor.current = ev.target.selectionStart;
        setValue(ev.target.value);
      },
      [setValue],
    ),
    onBlur: () => validate(),
    value: value || '',
    tabIndex: accordion?.isClosed ? -1 : 0,
  };

  return type === 'textarea' ? (
    <textarea {...inputProps} ref={inputRef}>
      {label}
    </textarea>
  ) : (
    <input {...inputProps} ref={inputRef} />
  );
};

/**
 * @typedef FieldProps
 * @extends React.HTMLAttributes<HTMLLabelElement>
 * @prop {string|ReactNode} label field label
 * @prop {string} [className] css class name (optional)
 * @prop {string} field field name
 * @prop {ReactNode} [children] (optional)
 * @prop {boolean} [required] (restructured to `isRequired`) (optional)
 */

/**
 * @component Field
 * @summary React Component for whole form field
 * @param {FieldProps} props
 * @returns {ReactNode} JSX/markup for whole field
 */
export const Field = (props) => {
  const { label, className = "", field, children, required: isRequired } = props;
  const { error, value, validate, setValue } = useField(field, isRequired);
  const inputProps = { value, validate, setValue };

  return (
    <label
      className={cx('checkout__input-group block x aic jcc mb05', className, {
        error,
      })}
    >
      <label className="block mb025">{label}</label>

      {children || <Input {...props} {...inputProps} />}

      {error && <code className="error">{error}</code>}
    </label>
  );
};

/**
 * @typedef ReducerAction
 * @prop {string} type type of reducer action
 * @prop {*} payload payload for given action
 */

/**
 * @typedef IntlPhoneInputProps
 * @extends React.InputHTMLAttributes<HTMLInputElement>
 * @prop {string} field field name
 * @prop {ReducerAction[]} [actions] actions to run (optional)
 * @prop {React.Dispatch} [dispatch] (optional)
 * @prop {*} rest rest of args (spread) (optional)
 */

/**
 * @component IntlPhoneInput
 * @summary React Component for international phone inputs w/ country select
 * @param {IntlPhoneInputProps} props
 * @returns {ReactNode} JSX/markup with input field
 */
export const IntlPhoneInput = ({ field, actions, dispatch, ...rest }) => {
  const inputRef = useRef();
  const [itiRef, iti, getItiError] = useIntlTelInput();
  const { value, setValue, setError, unsetError, error } = useField(field);

  const accordion = useAccordionContext();
  const tabIndex = accordion?.isClosed ? -1 : 0;

  const hasMounted = useRef(false);

  useEffect(() => {
    setTimeout(
      () => {
        const flagDropdown = inputRef.current.parentNode.querySelector(
          '.iti__selected-flag',
        );
        if (flagDropdown) {
          flagDropdown.tabIndex = tabIndex;
        }
      },
      hasMounted.current ? 0 : 500,
    );
  }, [tabIndex]);

  /* Also listen to changes in the country field */
  const { value: country, error: countryError } = useField('country');
  useEffect(() => {
    if (iti && country && !countryError && !iti.getNumber()) {
      iti.setCountry(country);
    }
  }, [country, countryError, iti]);

  /* Set phone number from prefill/form value, or clear when value is empty */
  useEffect(() => {
    if (!iti) return;

    if (value) {
      // Set number if form has a value and iti doesn't
      if (!iti.getNumber()) {
        iti.setNumber(value);
      }
    } else {
      // Clear iti when form value is empty (e.g., after reset)
      if (iti.getNumber()) {
        iti.setNumber('');
      }
    }
  }, [iti, value]);

  const handleChange = useCallback(() => {
    if (!iti) return;
    const val = iti.getNumber();
    setValue(val);

    if (val && error) setError(getItiError());
    else if (!error === false) unsetError();
  }, [iti, setValue, setError, unsetError, getItiError, error]);

  const handleBlur = useCallback(() => {
    if (!iti) return;
    const val = iti.getNumber();
    if (val) setError(getItiError());
  }, [iti, setError, getItiError]);

  const setRef = useCallback(
    (el) => {
      itiRef(el);
      inputRef.current = el;
    },
    [itiRef],
  );

  return (
    <input
      className="auth__input s16 x"
      type="text"
      name={field}
      tabIndex={tabIndex}
      onChange={handleChange}
      onBlur={handleBlur}
      ref={setRef}
      {...rest}
    />
  );
};
/**
 * @typedef CountryProps
 * @prop {string} value country code (two letter)
 * @prop {string} label country name
 */

/**
 * @func findCountryByLabel
 * @summary find country by case-insensitive name comparison
 * @param {string} label
 * @returns {CountryProps|false} country with match or `false`
 */
const findCountryByLabel = (label) =>
  label && countries.find((c) => c.label.toLowerCase() === label.toLowerCase());

/**
 * @func findCountryByCode
 * @summary find country by case-sensitive code
 * @param {string} code
 * @returns {CountryProps|false} country with match or `false`
 */
const findCountryByCode = (code) =>
  code && countries.find((c) => c.value === code);

/**
 * @component CountrySelect
 * @type {React.FC}
 * @summary React Component for country dropdown w/ autocomplete
 * @returns {ReactNode} JSX/markup with dropdown
 */
export const CountrySelect = () => {
  const { value: savedValue, setValue } = useField('country');
  const [renderAutoFillCatcher, setRenderAutoFillCatch] = useState(true);
  const hiddenInput = useRef(null);

  const accordion = useAccordionContext();

  /*
    The autofillcatcher needs to be removed and then remounted, whenever an
    autofill event happens
   */
  const catchAutofillUpdate = useCallback(
    (ev) => {
      const autoCountry = ev.target?.value?.toLowerCase();
      const country =
        findCountryByLabel(autoCountry) ||
        findCountryByCode(autoCountry?.toUpperCase());

      if (country) {
        setValue(country.value);
      }
      /* Remove the autofill catcher */
      setTimeout(() => setRenderAutoFillCatch(false), 0);
    },
    [setValue],
  );

  useEffect(() => {
    /* Remount the autofill catcher after a small timeout */
    if (!renderAutoFillCatcher) {
      setTimeout(() => setRenderAutoFillCatch(true), 64);
    }
  }, [renderAutoFillCatcher]);

  return (
    <>
      <Select
        options={countries}
        // defaultMenuIsOpen={true}
        onChange={(option) => setValue(option.value)}
        value={findCountryByCode(savedValue)}
        placeholder="Country*"
        className="country-select x"
        classNamePrefix="country-select"
        tabIndex={accordion?.isClosed ? -1 : 0}
      />

      {renderAutoFillCatcher && (
        <input
          name="country"
          id="hiddenCountryInput"
          ref={hiddenInput}
          className="hidden__country"
          tabIndex={-1}
          autoComplete="shipping country"
          onChange={catchAutofillUpdate}
        />
      )}
    </>
  );
};

/**
 * @func findTerritory
 * @summary Find territory by value (string) or id (number)
 * @param {string|number} val - Territory value or numeric ID
 * @returns {object|undefined} Territory option object
 */
const findTerritory = (val) => {
  if (!val) return undefined;
  // Try to find by value (string match)
  const byValue = territories.find((t) => t.value === val);
  if (byValue) return byValue;
  // Fallback: try to find by numeric id
  const numVal = Number(val);
  if (!Number.isNaN(numVal)) {
    return territories.find((t) => t.id === numVal);
  }
  return undefined;
};

/**
 * @component TerritorySelect
 * @type {React.FC}
 * @summary React Component to select territory (not country)
 * @returns {ReactNode} JSX/markup with dropdown
 */
export const TerritorySelect = () => {
  const { value: savedValue, setValue } = useField('territory');
  const accordion = useAccordionContext();
  const selectedTerritory = findTerritory(savedValue);
  return (
    <Select
      options={territories}
      // defaultMenuIsOpen={true}
      onChange={(option) => setValue(option.value)}
      value={selectedTerritory}
      placeholder="Territory"
      className="country-select x"
      tabIndex={accordion?.isClosed ? -1 : 0}
      classNamePrefix="country-select"
    />
  );
};

/**
 * @typedef SubmitButtonProps
 * @prop {Function} onClick click event handler
 * @prop {Function} onSubmit submit event handler (unused)
 * @prop {boolean} disabled is button disabled?
 * @prop {string} type button type (submit, reset)
 * @prop {ReactNode} children button children
 */

/**
 * @component SubmitButton
 * @type {React.FC<SubmitButtonProps>}
 * @summary React Component for submit button
 * @param {SubmitButtonProps} props
 * @returns {ReactNode} JSX/markup for submit button w/ icon
 */
export const SubmitButton = ({
  onClick = () => {},
  onSubmit,
  disabled,
  type = 'submit',
  children,
  className
}) => {
  const icons = {
    submit: (
      <svg
        fill="currentColor"
        version="1.1"
        id="Layer_1"
        x="0px"
        y="0px"
        viewBox="0 0 55 20"
      >
        <polygon
          fillRule="evenodd"
          clipRule="evenodd"
          points="43.3,0 55,10 43.3,20 42.4,19.1 52.2,10.7 0,10.7 0,9.3 52.2,9.3   42.4,0.9 43.3,0 "
        ></polygon>
      </svg>
    ),
    reset: (
      <svg
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M21.5 2v6h-6M2.5 22v-6h6M2 11.5a10 10 0 0 1 18.8-4.3M22 12.5a10 10 0 0 1-18.8 4.2"/>
      </svg>
    )
  };

  return (
    <button
      className={cx("if jcb aic mt1 mb05 hover block caps akz black", type === 'reset' ? 'checkout__clear-btn' : '','asset__add-to-cart', className)}
      type={type}
      disabled={disabled}
      tabIndex={0}
      onClick={onClick}
    >
      {children || 'Request Invoice'}
      <div className="asset__add-to-cart__arrow ml05">
        {type in icons ? icons[type] : null}
      </div>
    </button>
  );
};
